// src/pages/DetalhesCampanha.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
    ArrowLeftIcon,
    ArrowPathIcon,
    StopCircleIcon,
    ArrowDownTrayIcon,
    PaperAirplaneIcon,
    ExclamationTriangleIcon,
    UsersIcon,
    CheckCircleIcon,
    PhoneXMarkIcon,
    EyeIcon,
    ClockIcon,
    PauseCircleIcon,
    PlayCircleIcon,
    CalendarDaysIcon,
    UserCircleIcon,
    PhoneIcon,
    ChatBubbleBottomCenterTextIcon,
    CalendarIcon,
} from '@heroicons/react/24/outline';
import CallLogDetailsModal, { traduzirEndedReason, getStatusBadgeClass } from '../components/CallLogDetailsModal';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getBearerHeaders } from '../lib/auth';

const selectClassName = 'h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

// Função para traduzir detalhes para português
const traduzirDetalhes = (detalhes) => {
    if (!detalhes) return '-';
    
    const texto = String(detalhes).toLowerCase();
    
    // Traduções de encerramentos normais
    if (texto.includes('customer-ended-call') || texto.includes('customer ended call')) {
        return detalhes.replace(/customer-ended-call|customer ended call/gi, 'Cliente encerrou a chamada');
    }
    if (texto.includes('assistant-ended-call') || texto.includes('assistant ended call')) {
        return detalhes.replace(/assistant-ended-call|assistant ended call/gi, 'Assistente encerrou a chamada');
    }
    if (texto.includes('customer-did-not-answer') || texto.includes('customer did not answer')) {
        return detalhes.replace(/customer-did-not-answer|customer did not answer/gi, 'Cliente não atendeu');
    }
    if (texto.includes('customer-busy') || texto.includes('customer busy')) {
        return detalhes.replace(/customer-busy|customer busy/gi, 'Cliente ocupado');
    }
    if (texto.includes('silence-timed-out') || texto.includes('silence timed out')) {
        return detalhes.replace(/silence-timed-out|silence timed out/gi, 'Silêncio prolongado (timeout)');
    }
    
    // Traduções gerais
    return detalhes
        .replace(/\bcustomer\b/gi, 'Cliente')
        .replace(/\bended\b/gi, 'encerrou')
        .replace(/\bcall\b/gi, 'chamada')
        .replace(/\bassistant\b/gi, 'Assistente')
        .replace(/\bdid\b/gi, '')
        .replace(/\bnot\b/gi, 'não')
        .replace(/\banswer\b/gi, 'atendeu')
        .replace(/\bbusy\b/gi, 'ocupado')
        .replace(/\bfailed\b/gi, 'falhou')
        .replace(/\berror\b/gi, 'erro')
        .replace(/\btimeout\b/gi, 'tempo esgotado')
        .replace(/\bconnection\b/gi, 'conexão')
        .replace(/\bunavailable\b/gi, 'indisponível');
};

function normalizePhoneDigits(s) {
    return String(s || '').replace(/\D/g, '');
}

function formatPhoneForDisplay(raw) {
    const digits = normalizePhoneDigits(raw);
    if (!digits) return String(raw || '');
    if (String(raw || '').trim().startsWith('+')) return `+${digits}`;
    if (digits.startsWith('55')) return `+${digits}`;
    if (digits.length >= 10) return `+55${digits}`;
    return digits;
}

function normalizeSearchText(value) {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
}

function toSearchableText(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch (_e) {
        return String(value);
    }
}

function flattenStructuredOutput(value, prefix = '', target = {}) {
    if (Array.isArray(value)) {
        if (value.length === 0) {
            target[prefix || 'valor'] = '';
            return target;
        }
        value.forEach((item, index) => {
            const key = prefix ? `${prefix}.${index + 1}` : String(index + 1);
            flattenStructuredOutput(item, key, target);
        });
        return target;
    }

    if (value && typeof value === 'object') {
        const entries = Object.entries(value);
        if (entries.length === 0) {
            target[prefix || 'valor'] = '';
            return target;
        }
        entries.forEach(([key, nestedValue]) => {
            const composedKey = prefix ? `${prefix}.${key}` : key;
            flattenStructuredOutput(nestedValue, composedKey, target);
        });
        return target;
    }

    target[prefix || 'valor'] = value ?? '';
    return target;
}

function formatStructuredCellValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch (_e) {
        return String(value);
    }
}

function buildStructuredOutputColumns(rawStructuredOutput) {
    if (!rawStructuredOutput || typeof rawStructuredOutput !== 'object' || Array.isArray(rawStructuredOutput)) {
        return {};
    }

    const values = Object.values(rawStructuredOutput);
    const looksLikeNamedStructures =
        values.length > 0 &&
        values.every((item) => item && typeof item === 'object' && !Array.isArray(item) && item.name);

    if (looksLikeNamedStructures) {
        const columns = {};
        values.forEach((item, index) => {
            const baseName = String(item.name || '').trim() || `Estrutura ${index + 1}`;
            let columnName = baseName;
            let suffix = 2;
            while (Object.prototype.hasOwnProperty.call(columns, columnName)) {
                columnName = `${baseName} (${suffix})`;
                suffix += 1;
            }

            const preferredValue =
                item.result ??
                item.output ??
                item.value ??
                item.answer ??
                '';

            columns[columnName] = preferredValue;
        });
        return columns;
    }

    return flattenStructuredOutput(rawStructuredOutput);
}

/** Chave em followUps pode diferir do contato (+55 vs dígitos só). */
function getFollowUpEntryForContato(followUps, numero) {
    if (!followUps || typeof followUps !== 'object') return null;
    const direct = followUps[numero];
    if (direct != null && typeof direct === 'object') return direct;
    const d = normalizePhoneDigits(numero);
    for (const k of Object.keys(followUps)) {
        if (normalizePhoneDigits(k) === d) {
            const v = followUps[k];
            if (v != null && typeof v === 'object') return v;
        }
    }
    return null;
}

function getTentativasForContato(tentativas, numero) {
    if (tentativas == null) return 0;
    if (tentativas instanceof Map) {
        const d = normalizePhoneDigits(numero);
        for (const [k, v] of tentativas.entries()) {
            if (normalizePhoneDigits(k) === d) return Number(v) || 0;
        }
        return 0;
    }
    if (typeof tentativas === 'object' && !Array.isArray(tentativas)) {
        if (tentativas[numero] != null) return Number(tentativas[numero]) || 0;
        const d = normalizePhoneDigits(numero);
        for (const k of Object.keys(tentativas)) {
            if (normalizePhoneDigits(k) === d) return Number(tentativas[k]) || 0;
        }
    }
    return 0;
}

/** Denominador = config atual da campanha; numerador = persistido ou estimativa (tentativas gerais − 1ª ligação). */
function getFollowUpDisplayForContato(campanha, numero) {
    const fuCfg = campanha?.followUpConfig || {};
    const followUpInfo = getFollowUpEntryForContato(campanha?.followUps, numero);
    const maxFu = Number(fuCfg.maxTentativasTotal ?? followUpInfo?.maxTentativas ?? 4);
    if (fuCfg.habilitado === false) return { text: '—', maxFu };
    if (!followUpInfo) return { text: '—', maxFu };
    const rawFu = Number(followUpInfo.tentativasFollowUpTotal ?? 0);
    const tg = getTentativasForContato(campanha?.tentativas, numero);
    let displayFu = rawFu;
    if (rawFu === 0 && tg > 1) {
        displayFu = Math.min(tg - 1, maxFu);
    }
    return { text: `${displayFu}/${maxFu}`, maxFu, displayFu };
}

// Componente de Badge para Status
const StatusBadge = ({ status }) => {
    const styles = {
        em_fila: 'bg-yellow-500 text-yellow-100',
        reenviada: 'bg-orange-500 text-orange-100',
        atendido: 'bg-green-500 text-green-100',
        falha: 'bg-red-500 text-red-100',
        falhou: 'bg-red-600 text-red-100',
        cancelada: 'bg-gray-500 text-gray-100',
        pausada: 'bg-amber-500 text-amber-100',
        // Status antigos (compatibilidade com dados existentes)
        pendente: 'bg-yellow-500 text-yellow-100',
        concluída: 'bg-green-500 text-green-100',
        concluida: 'bg-green-500 text-green-100',
        sucesso: 'bg-green-500 text-green-100',
        erro: 'bg-red-600 text-red-100',
        em_progresso: 'bg-blue-500 text-blue-100',
        em_andamento: 'bg-purple-500 text-purple-100',
        enviado: 'bg-blue-500 text-blue-100',
        follow_up_agendado: 'bg-indigo-500 text-indigo-100',
    };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || styles.cancelada}`}>{status}</span>;
};

// Componente de Card de Estatística
const StatCard = ({ title, value, icon }) => (
    <div className="bg-gray-800 bg-opacity-60 p-4 rounded-xl flex items-center">
        <div className="p-3 bg-purple-600 bg-opacity-30 rounded-lg mr-4">{icon}</div>
        <div>
            <p className="text-sm text-gray-400">{title}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
        </div>
    </div>
);

export default function DetalhesCampanha() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [campanha, setCampanha] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('contatos');
    const [campaignCallLogs, setCampaignCallLogs] = useState([]);
    const [campaignCallLogsLoading, setCampaignCallLogsLoading] = useState(false);
    const [selectedCampaignCall, setSelectedCampaignCall] = useState(null);
    const [assistants, setAssistants] = useState([]);
    const [phoneNumbersList, setPhoneNumbersList] = useState([]);
    const [campaignLogsCurrentPage, setCampaignLogsCurrentPage] = useState(1);
    const [campaignLogsPagination, setCampaignLogsPagination] = useState({ total: 0, total_pages: 0 });
    const [logsFilterCustomerNumber, setLogsFilterCustomerNumber] = useState('');
    const [messageLogs, setMessageLogs] = useState([]);
    const [messageLogsLoading, setMessageLogsLoading] = useState(false);
    const [variaveisSearch, setVariaveisSearch] = useState('');
    // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';

    const getAuthConfig = () => {
        const headers = getBearerHeaders();
        if (!headers) return null;
        return { headers };
    };
    const campaignLogsPerPage = 10;

    const fetchCampaignDetails = useCallback(async () => {
        let isMounted = true;
        setLoading(true);
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) {
                setError('Sessão expirada. Faça login novamente.');
                toast.error('Sessão expirada. Faça login novamente.');
                setLoading(false);
                return () => { isMounted = false };
            }

            const res = await axios.get(`${baseUrl}/api/campanhas/${id}`, authConfig);
            if (isMounted) {
                const campanhaData = res.data;
                const cid = campanhaData.clienteId;
                const clienteIdResolved = cid ? (typeof cid === 'object' && cid._id ? cid._id : cid) : null;
                const assistentesParams = clienteIdResolved ? { clienteId: String(clienteIdResolved) } : {};
                const tokenClient = localStorage.getItem('tokenClient');
                const catalogCfg = {
                    params: { ...assistentesParams, ...(tokenClient ? { tokenClient } : {}) },
                    ...authConfig,
                };
                const [assistentesRes, phonesRes] = await Promise.all([
                    axios.get(`${baseUrl}/api/assistentes/master`, catalogCfg).catch(() => ({ data: [] })),
                    axios.get(`${baseUrl}/api/phoneNumbers`, catalogCfg).catch(() => ({ data: [] })),
                ]);
                setAssistants(Array.isArray(assistentesRes.data) ? assistentesRes.data : []);
                setPhoneNumbersList(Array.isArray(phonesRes.data) ? phonesRes.data : []);

                setCampanha(campanhaData);
                setError('');
            }
        } catch (err) {
            if (isMounted) {
                setError('Erro ao carregar os detalhes da campanha.');
                toast.error('Não foi possível carregar a campanha.');
                console.error(err);
            }
        } finally {
            if (isMounted) setLoading(false);
        }
        return () => { isMounted = false };
    }, [id, baseUrl]);

    useEffect(() => {
        fetchCampaignDetails();
    }, [fetchCampaignDetails]);

    // Buscar chamadas (call reports) da campanha com filtros e paginação
    const fetchCampaignCallLogs = useCallback(async () => {
        if (!id || !campanha) return;
        setCampaignCallLogsLoading(true);
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) {
                toast.error('Sessão expirada. Faça login novamente.');
                setCampaignCallLogsLoading(false);
                return;
            }

            const params = {
                campaignId: id,
                page: campaignLogsCurrentPage,
                per_page: campaignLogsPerPage,
            };
            if (logsFilterCustomerNumber.trim()) params.customerNumber = logsFilterCustomerNumber.trim();

            const cid = campanha.clienteId;
            const clienteId = cid ? (typeof cid === 'object' && cid._id ? cid._id : cid) : null;
            const assistentesParams = clienteId ? { clienteId: String(clienteId) } : {};
            const [logsRes, assistantsRes] = await Promise.all([
                axios.get(`${baseUrl}/api/callReports`, { params, ...authConfig }),
                axios.get(`${baseUrl}/api/assistentes/master`, { params: assistentesParams, ...authConfig }).catch(() => ({ data: [] })),
            ]);
            setCampaignCallLogs(logsRes.data?.data || []);
            setCampaignLogsPagination({
                total: logsRes.data?.pagination?.total ?? 0,
                total_pages: logsRes.data?.pagination?.total_pages ?? 0,
            });
            setAssistants(Array.isArray(assistantsRes.data) ? assistantsRes.data : []);
        } catch (err) {
            console.error('[DetalhesCampanha] Erro ao buscar logs da campanha:', err);
            toast.error('Não foi possível carregar as chamadas da campanha.');
            setCampaignCallLogs([]);
        } finally {
            setCampaignCallLogsLoading(false);
        }
    }, [id, campanha, baseUrl, campaignLogsCurrentPage, campaignLogsPerPage, logsFilterCustomerNumber]);

    useEffect(() => {
        if (activeTab === 'logs' && campanha) {
            fetchCampaignCallLogs();
        }
    }, [activeTab, campanha, fetchCampaignCallLogs]);

    const fetchCampaignMessageLogs = useCallback(async () => {
        if (!id || activeTab !== 'logs') return;
        setMessageLogsLoading(true);
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) {
                setMessageLogsLoading(false);
                return;
            }
            const res = await axios.get(`${baseUrl}/api/campanhas/${id}/followup-message-logs`, {
                params: { page: 1, per_page: 100 },
                ...authConfig,
            });
            setMessageLogs(Array.isArray(res.data?.data) ? res.data.data : []);
        } catch (err) {
            console.error('[DetalhesCampanha] Erro ao buscar histórico de mensagens:', err);
            setMessageLogs([]);
        } finally {
            setMessageLogsLoading(false);
        }
    }, [id, activeTab, baseUrl]);

    useEffect(() => {
        if (activeTab === 'logs') {
            fetchCampaignMessageLogs();
        }
    }, [activeTab, fetchCampaignMessageLogs]);

    const setLogsFilterAndResetPage = (setter, value) => {
        setter(value);
        setCampaignLogsCurrentPage(1);
    };

    const stats = useMemo(() => {
        if (!campanha) return { total: 0, sucesso: 0, falha: 0, emFila: 0 };
        // Contar contatos com status de sucesso: concluída, atendido, sucesso
        const sucessoCount = Object.values(campanha.resultado || {}).filter(r => {
            const status = r.status?.toLowerCase();
            return status === 'atendido' || status === 'concluída' || status === 'concluida' || status === 'sucesso';
        }).length;
        // Contar contatos com status de falha: erro, falha, falhou
        const falhaCount = Object.values(campanha.resultado || {}).filter(r => {
            const status = r.status?.toLowerCase();
            return status === 'erro' || status === 'falha' || status === 'falhou';
        }).length;
        // Contar contatos com status em_fila
        const emFilaCount = Object.values(campanha.resultado || {}).filter(r => {
            const status = r.status?.toLowerCase();
            return status === 'em_fila' || status === 'pendente';
        }).length;
        // Contar também contatos sem resultado (que estão em_fila por padrão)
        const totalContatos = campanha.contatos?.length || 0;
        const contatosComResultado = Object.keys(campanha.resultado || {}).length;
        const contatosSemResultado = totalContatos - contatosComResultado;
        const totalEmFila = emFilaCount + contatosSemResultado;
        
        return {
            total: totalContatos,
            sucesso: sucessoCount,
            falha: falhaCount,
            emFila: totalEmFila,
        };
    }, [campanha]);

    const formatDuration = (seconds) => {
        if (seconds === null || seconds === undefined || isNaN(seconds)) return 'N/A';
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Data inválida';
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        } catch (e) {
            return 'N/A';
        }
    };
    const findAssistantById = (assistantId) =>
        assistants.find(a => (a.vapiId || a.id || a._id) === assistantId);
    const findPhoneById = (phoneId) =>
        phoneNumbersList.find(p => String(p.vapiId || p._id) === String(phoneId));

    const followUpConfigResumo = useMemo(() => {
        if (!campanha) return null;
        const cfg = campanha.followUpConfig || {};
        const campAsst = campanha.sip?.phoneNumberBody?.assistantId;
        const campPn = campanha.sip?.phoneNumberBody?.phoneNumberId;
        const effAsst = cfg.assistenteFollowUpId || campAsst;
        const effPn = cfg.phoneNumberFollowUpId || campPn;
        const asstEff = findAssistantById(effAsst);
        const pnEff = findPhoneById(effPn);
        const nomeCampanhaAsst = campanha.detalhesEnriquecidos?.assistente?.nome;
        const numCampanha = campanha.detalhesEnriquecidos?.telefone;
        return {
            ativo: cfg.habilitado !== false,
            maxTotal: cfg.maxTentativasTotal ?? 4,
            maxDia: cfg.maxTentativasPorDia ?? 1,
            intervaloMin: cfg.intervaloMinutos ?? 1440,
            assistenteTexto: cfg.assistenteFollowUpId
                ? (asstEff?.nome || asstEff?.name || cfg.assistenteFollowUpId)
                : `Mesmo da campanha${nomeCampanhaAsst ? ` (${nomeCampanhaAsst})` : ''}`,
            numeroTexto: cfg.phoneNumberFollowUpId
                ? (pnEff ? `${pnEff.name} (${pnEff.number})` : effPn)
                : `Mesmo da campanha${numCampanha?.number ? ` (${numCampanha.number}${numCampanha.name ? ` — ${numCampanha.name}` : ''})` : ''}`,
        };
    }, [campanha, assistants, phoneNumbersList]);

    const campaignDisplayLogs = useMemo(() => {
        return (campaignCallLogs || []).map(log => {
            const assistantId = log.assistant?.id || log.call?.assistantId || log.assistantId || log.call?.assistant?.id;
            const assistant = findAssistantById(assistantId);
            const customerNumber = log.call?.customer?.number || log.customer?.number || '';
            const fuDispLog = customerNumber && campanha ? getFollowUpDisplayForContato(campanha, customerNumber) : { text: '—' };
            const startedAt = log.startedAt || log.call?.startedAt || log.call?.createdAt || log.timestamp;
            let durationSeconds = log.durationSeconds;
            if (!durationSeconds && log.durationMs) durationSeconds = Math.floor(log.durationMs / 1000);
            else if (!durationSeconds && log.durationMinutes) durationSeconds = Math.floor(log.durationMinutes * 60);
            const endedReason = log.endedReason || log.call?.endedReason;
            const cost = log.cost || log.call?.cost || 0;
            const custoFinal = log.custoFinal !== undefined && log.custoFinal !== null ? log.custoFinal : cost;
            return {
                ...log,
                assistantName: assistant?.nome || assistant?.name || 'N/A',
                formattedDate: formatDateTime(startedAt),
                endedReason,
                durationSeconds: durationSeconds ?? null,
                custoFinal,
                customerNumber,
                followUpDisplayText: fuDispLog.text,
            };
        });
    }, [campaignCallLogs, assistants, campanha]);

    const variaveisDinamicas = useMemo(() => {
        const rows = Array.isArray(campanha?.dadosPlanilha) ? campanha.dadosPlanilha : [];
        const normalizedRows = rows.filter((row) => row && typeof row === 'object' && !Array.isArray(row));

        const columnsSet = new Set();
        normalizedRows.forEach((row) => {
            Object.keys(row).forEach((key) => {
                if (key !== '_origemIntegracao') {
                    columnsSet.add(key);
                }
            });
        });

        const columns = Array.from(columnsSet);
        const search = normalizeSearchText(variaveisSearch);
        const filteredRows = !search
            ? normalizedRows
            : normalizedRows.filter((row) =>
                columns.some((col) => normalizeSearchText(toSearchableText(row[col])).includes(search))
            );

        return {
            columns,
            rows: filteredRows,
            totalRows: normalizedRows.length,
        };
    }, [campanha?.dadosPlanilha, variaveisSearch]);

    const handleAction = async (actionFn, successMsg, errorMsg) => {
        const toastId = toast.loading('Processando sua solicitação...');
        try {
            await actionFn();
            if (toastId) toast.success(successMsg, { id: toastId });
            await fetchCampaignDetails();
        } catch (err) {
            if (toastId) toast.error(errorMsg, { id: toastId });
            console.error(err);
        }
    };

    const reenviarCampanha = () => handleAction(
        () => {
            const authConfig = getAuthConfig();
            if (!authConfig) throw new Error('Sessão expirada');
            return axios.post(`${baseUrl}/api/campanhas/${id}/reenviar`, {}, authConfig);
        },
        'Comando de reenvio enviado!',
        'Erro ao reenviar campanha.'
    );

    const reenviarFalhas = () => handleAction(
        () => {
            const authConfig = getAuthConfig();
            if (!authConfig) throw new Error('Sessão expirada');
            return axios.post(`${baseUrl}/api/campanhas/${id}/reenviar-falhas`, {}, authConfig);
        },
        'Comando de reenvio de falhas enviado!',
        'Erro ao reenviar falhas.'
    );

    const cancelarCampanha = () => handleAction(
        () => {
            const authConfig = getAuthConfig();
            if (!authConfig) throw new Error('Sessão expirada');
            return axios.post(`${baseUrl}/api/campanhas/${id}/cancelar`, {}, authConfig);
        },
        'Campanha cancelada com sucesso.',
        'Erro ao cancelar campanha.'
    );

    const pausarCampanha = () => handleAction(
        () => {
            const authConfig = getAuthConfig();
            if (!authConfig) throw new Error('Sessão expirada');
            return axios.post(`${baseUrl}/api/campanhas/${id}/pausar`, {}, authConfig);
        },
        'Campanha pausada com sucesso.',
        'Erro ao pausar campanha.'
    );

    const despausarCampanha = () => handleAction(
        () => {
            const authConfig = getAuthConfig();
            if (!authConfig) throw new Error('Sessão expirada');
            return axios.post(`${baseUrl}/api/campanhas/${id}/despausar`, {}, authConfig);
        },
        'Campanha despausada com sucesso.',
        'Erro ao despausar campanha.'
    );

    const reenviarContato = (numero) => handleAction(
        () => {
            const authConfig = getAuthConfig();
            if (!authConfig) throw new Error('Sessão expirada');
            return axios.post(`${baseUrl}/api/campanhas/${id}/reenviar`, { numero }, authConfig);
        },
        `Reenvio solicitado para ${numero}.`,
        'Erro ao reenviar para o contato.'
    );

    const openCallDetailsModal = async (callId) => {
        if (!callId) return;
        const toastId = toast.loading('Carregando detalhes da chamada...');
        try {
            const authConfig = getAuthConfig() || {};
            const res = await axios.get(`${baseUrl}/api/callReports/${callId}`, authConfig);
            const callData = res.data;
            const assistantId = callData.assistant?.id || callData.call?.assistantId || callData.assistantId;
            const assistant = findAssistantById(assistantId);
            setSelectedCampaignCall({
                ...callData,
                assistantName: assistant?.nome || assistant?.name || callData.assistant?.name || 'N/A',
            });
        } catch (err) {
            toast.error('Não foi possível carregar os detalhes da chamada.', { id: toastId });
            console.error('[DetalhesCampanha] Erro ao buscar call report:', err);
        } finally {
            toast.dismiss(toastId);
        }
    };

    const exportar = async (format) => {
        if (!campanha) return;
        const toastId = toast.loading(`Exportando para ${format.toUpperCase()}...`);
        try {
            const resultadoObj = campanha.resultado instanceof Map
                ? Object.fromEntries(campanha.resultado)
                : (campanha.resultado || {});

            // Buscar todos os call reports da campanha (paginado para não limitar a 10)
            const authConfig = getAuthConfig() || {};
            let allCallReports = [];
            let page = 1;
            let hasMore = true;
            while (hasMore) {
                const res = await axios.get(`${baseUrl}/api/callReports`, {
                    params: { campaignId: id, page, per_page: 100 },
                    ...authConfig,
                });
                const list = res.data?.data || [];
                allCallReports = allCallReports.concat(list);
                const total = res.data?.pagination?.total ?? 0;
                hasMore = allCallReports.length < total;
                page++;
            }

            // Mapa: número do cliente -> call report (e também por call.id)
            const callByNumber = {};
            const callById = {};
            allCallReports.forEach(call => {
                const num = call.call?.customer?.number || call.customer?.number || '';
                if (num) callByNumber[num] = call;
                const cid = call.call?.id || call._id;
                if (cid) callById[cid] = call;
            });

            const getTranscript = (call) => {
                if (!call) return '';
                return call.transcript || call.artifact?.transcript || '';
            };
            const getStructuredOutputs = (call) => {
                if (!call) return {};
                const out = call.structuredOutputs || call.artifact?.structuredOutputs || {};
                if (!out || typeof out !== 'object' || Object.keys(out).length === 0) return {};
                return buildStructuredOutputColumns(out);
            };
            const formatCallDate = (call) => {
                if (!call) return '';
                const d = call.startedAt || call.call?.startedAt || call.call?.createdAt;
                return d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '';
            };
            const getDuration = (call) => {
                if (!call) return '';
                const s = call.durationSeconds ?? (call.durationMs ? Math.floor(call.durationMs / 1000) : null);
                if (s == null) return '';
                const m = Math.floor(s / 60);
                const sec = Math.floor(s % 60);
                return `${m}:${sec.toString().padStart(2, '0')}`;
            };

            if (format === 'xlsx') {
                const wb = XLSX.utils.book_new();

                // Calcular estatísticas (atendidas, falhas, em fila)
                const resultadoValues = Object.values(resultadoObj);
                const sucessoCount = resultadoValues.filter(r => {
                    const s = r?.status?.toLowerCase();
                    return s === 'atendido' || s === 'concluída' || s === 'concluida' || s === 'sucesso';
                }).length;
                const falhaCount = resultadoValues.filter(r => {
                    const s = r?.status?.toLowerCase();
                    return s === 'erro' || s === 'falha' || s === 'falhou';
                }).length;
                const emFilaCount = resultadoValues.filter(r => {
                    const s = r?.status?.toLowerCase();
                    return s === 'em_fila' || s === 'pendente';
                }).length;
                const totalContatos = campanha.contatos?.length || 0;
                const contatosSemResultado = totalContatos - resultadoValues.length;
                const totalEmFila = emFilaCount + contatosSemResultado;

                // Aba Resumo da Campanha
                const resumoData = [
                    { Campo: 'Nome', Valor: campanha.nome },
                    { Campo: 'Objetivo', Valor: campanha.objetivo || '-' },
                    { Campo: 'Status', Valor: campanha.status || '-' },
                    { Campo: 'Criado em', Valor: campanha.criadoEm ? new Date(campanha.criadoEm).toLocaleString('pt-BR') : '-' },
                    { Campo: 'Total de contatos', Valor: totalContatos.toString() },
                    { Campo: 'Ligações atendidas', Valor: sucessoCount.toString() },
                    { Campo: 'Ligações que falharam', Valor: falhaCount.toString() },
                    { Campo: 'Em fila', Valor: totalEmFila.toString() },
                ];
                const wsResumo = XLSX.utils.json_to_sheet(resumoData);
                XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo Campanha');

                // Aba Contatos e Chamadas (com transcrição e estruturas de saída em colunas separadas)
                const contatosComDados = campanha.contatos?.map(numero => {
                    const info = resultadoObj[numero] || {};
                    const callId = info.callId || info.response?.id || info.response?.data?.id;
                    const call = callId ? (callById[callId] || callByNumber[numero]) : callByNumber[numero];
                    const structuredFlat = getStructuredOutputs(call);
                    return {
                        numero,
                        tentativas: campanha.tentativas?.[numero] ?? 0,
                        status: info.status || '-',
                        detalhes: traduzirDetalhes(info.detalhes) || '-',
                        callId: callId || '-',
                        dataChamada: formatCallDate(call),
                        duracao: getDuration(call),
                        statusFinal: call ? traduzirEndedReason(call.endedReason || call.call?.endedReason) : '-',
                        transcricao: getTranscript(call),
                        structuredFlat,
                    };
                }) || [];

                const structuredColumns = Array.from(new Set(
                    contatosComDados.flatMap((item) => Object.keys(item.structuredFlat || {}))
                ));

                const rows = contatosComDados.map((item) => {
                    const row = {
                        'Número': item.numero,
                        'Tentativas': item.tentativas,
                        'Status': item.status,
                        'Detalhes': item.detalhes,
                        'ID Chamada': item.callId,
                        'Data da chamada': item.dataChamada,
                        'Duração': item.duracao,
                        'Status final (chamada)': item.statusFinal,
                        'Transcrição': item.transcricao,
                    };

                    structuredColumns.forEach((columnKey) => {
                        row[`Estrutura: ${columnKey}`] = formatStructuredCellValue(item.structuredFlat?.[columnKey]);
                    });

                    return row;
                });

                const wsContatos = XLSX.utils.json_to_sheet(rows);
                wsContatos['!cols'] = [
                    { wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 25 },
                    { wch: 38 }, { wch: 18 }, { wch: 8 }, { wch: 28 },
                    { wch: 50 },
                    ...structuredColumns.map((columnKey) => ({ wch: Math.max(24, Math.min(50, columnKey.length + 16)) })),
                ];
                XLSX.utils.book_append_sheet(wb, wsContatos, 'Contatos e Chamadas');

                XLSX.writeFile(wb, `Campanha-${campanha.nome?.replace(/[/\\?*\[\]]/g, '-') || id}.xlsx`);
            }
            toast.success('Exportação concluída!', { id: toastId });
        } catch (err) {
            console.error('[DetalhesCampanha] Erro ao exportar:', err);
            toast.error('Erro ao exportar. Tente novamente.', { id: toastId });
        }
    };

    const renderCellValue = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (_e) {
                return String(value);
            }
        }
        return String(value);
    };

    if (loading) {
        return <div className="p-8 text-white text-center">Carregando detalhes da campanha...</div>;
    }

    if (error || !campanha) {
        return <div className="p-8 text-center text-red-400"><ExclamationTriangleIcon className="h-6 w-6 inline mr-2" />{error || 'Campanha não encontrada.'}</div>;
    }

  return (
    <div className="space-y-6">
            <CallLogDetailsModal
                call={selectedCampaignCall}
                onClose={() => setSelectedCampaignCall(null)}
            />
            {/* Cabeçalho */}
            <div className="flex items-center mb-6">
                <button onClick={() => navigate('/campanhas')} className="p-2 mr-4 text-gray-400 hover:text-white rounded-full hover:bg-gray-700">
                    <ArrowLeftIcon className="h-6 w-6" />
                </button>
                <div>
                    <h1 className="text-3xl font-bold">{campanha.nome}</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <StatusBadge status={campanha.status} />
                        <span className="text-sm text-gray-400">Criada em: {new Date(campanha.criadoEm).toLocaleString()}</span>
                    </div>
                    {campanha.objetivo && (
                        <p className="text-sm text-gray-400 mt-1">Objetivo: {campanha.objetivo}</p>
                    )}
                </div>
            </div>
            {/* Ações da Campanha */}
            <div className="flex items-center gap-2 mb-6">
                {campanha.status === 'pausada' ? (
                    <button onClick={despausarCampanha} className="flex items-center px-4 py-2 bg-green-600 rounded-lg font-semibold hover:bg-green-700">
                        <PlayCircleIcon className="h-5 w-5 mr-2" />Despausar Campanha
                    </button>
                ) : (
                    <button onClick={pausarCampanha} className="flex items-center px-4 py-2 bg-amber-600 rounded-lg font-semibold hover:bg-amber-700">
                        <PauseCircleIcon className="h-5 w-5 mr-2" />Pausar Campanha
                    </button>
                )}
                <button onClick={reenviarCampanha} className="flex items-center px-4 py-2 bg-blue-600 rounded-lg font-semibold hover:bg-blue-700"><ArrowPathIcon className="h-5 w-5 mr-2" />Reenviar Pendentes</button>
                <button onClick={reenviarFalhas} className="flex items-center px-4 py-2 bg-orange-600 rounded-lg font-semibold hover:bg-orange-700"><ArrowPathIcon className="h-5 w-5 mr-2" />Reenviar Falhas</button>
                <button onClick={cancelarCampanha} className="flex items-center px-4 py-2 bg-red-600 rounded-lg font-semibold hover:bg-red-700"><StopCircleIcon className="h-5 w-5 mr-2" />Cancelar Campanha</button>
            </div>
            {/* Detalhes da Campanha */}
            <div className="bg-gray-800 bg-opacity-60 rounded-xl p-4 mb-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CalendarDaysIcon className="h-5 w-5 text-purple-400" />
                    Detalhes da Campanha
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Horário de Agendamento</p>
                        <p className="text-white font-medium">
                            {campanha.agendamento
                                ? new Date(campanha.agendamento).toLocaleString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })
                                : '—'}
                        </p>
                    </div>
                    <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                            <UserCircleIcon className="h-4 w-4" /> Assistente
                        </p>
                        <p className="text-white font-medium">
                            {campanha.detalhesEnriquecidos?.assistente?.nome || campanha.sip?.phoneNumberBody?.assistantId || '—'}
                        </p>
                    </div>
                    <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                            <PhoneIcon className="h-4 w-4" /> Telefone (Bina)
                        </p>
                        <p className="text-white font-medium">
                            {campanha.detalhesEnriquecidos?.telefone?.number
                                ? `${campanha.detalhesEnriquecidos.telefone.number}${campanha.detalhesEnriquecidos.telefone.name ? ` (${campanha.detalhesEnriquecidos.telefone.name})` : ''}`
                                : campanha.sip?.phoneNumberBody?.number || '—'}
                        </p>
                    </div>
                    {campanha.funcionamento?.diasSemana?.length > 0 && (
                        <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg md:col-span-full">
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Horário de Funcionamento</p>
                            <p className="text-white font-medium text-sm">
                                {campanha.funcionamento.dataInicio || campanha.funcionamento.dataFim
                                    ? `${campanha.funcionamento.dataInicio ? new Date(campanha.funcionamento.dataInicio).toLocaleDateString('pt-BR') : '...'} a ${campanha.funcionamento.dataFim ? new Date(campanha.funcionamento.dataFim).toLocaleDateString('pt-BR') : '...'}`
                                    : ''}
                                {campanha.funcionamento.dataInicio || campanha.funcionamento.dataFim ? ' · ' : ''}
                                {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].filter((_, i) => (campanha.funcionamento?.diasSemana || []).includes(i)).join(', ')}
                                {' · '}{campanha.funcionamento.horaInicio || '08:00'} às {campanha.funcionamento.horaFim || '18:00'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
            {followUpConfigResumo && (
                <div className="bg-gray-800 bg-opacity-60 rounded-xl p-4 mb-6 border border-indigo-500/30">
                    <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <ArrowPathIcon className="h-5 w-5 text-indigo-400" />
                        Follow-up da campanha
                    </h2>
                    <p className="text-xs text-gray-400 mb-3">
                        Contagem na tabela (coluna Follow-up): tentativas de <span className="text-gray-300">recontato</span> já disparadas pelo agendador, sobre o máximo configurado.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                        <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg">
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Status</p>
                            <p className="text-white font-medium">{followUpConfigResumo.ativo ? 'Ativo' : 'Desativado'}</p>
                        </div>
                        <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg">
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Máximo total / por dia</p>
                            <p className="text-white font-medium">
                                {followUpConfigResumo.maxTotal} total · {followUpConfigResumo.maxDia} por dia
                            </p>
                        </div>
                        <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg">
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Intervalo entre tentativas</p>
                            <p className="text-white font-medium">{followUpConfigResumo.intervaloMin} min</p>
                        </div>
                        <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg md:col-span-1">
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                                <UserCircleIcon className="h-4 w-4" /> Assistente no follow-up
                            </p>
                            <p className="text-white font-medium text-sm">{followUpConfigResumo.assistenteTexto}</p>
                        </div>
                        <div className="bg-gray-700 bg-opacity-40 p-3 rounded-lg md:col-span-2">
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                                <PhoneIcon className="h-4 w-4" /> Número (bina) no follow-up
                            </p>
                            <p className="text-white font-medium text-sm">{followUpConfigResumo.numeroTexto}</p>
                        </div>
                    </div>
                </div>
            )}
            {/* Estatísticas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <StatCard title="Total de Contatos" value={stats.total} icon={<UsersIcon className="h-6 w-6 text-purple-400" />} />
                <StatCard title="Em Fila" value={stats.emFila} icon={<ClockIcon className="h-6 w-6 text-yellow-400" />} />
                <StatCard title="Chamadas com Sucesso" value={stats.sucesso} icon={<CheckCircleIcon className="h-6 w-6 text-green-400" />} />
                <StatCard title="Chamadas com Falha" value={stats.falha} icon={<PhoneXMarkIcon className="h-6 w-6 text-red-400" />} />
            </div>
            {/* Abas e Conteúdo */}
            <div className="bg-gray-800 bg-opacity-60 rounded-xl shadow-lg">
                <div className="border-b border-gray-700">
                    <nav className="flex space-x-4 p-4">
                        <button onClick={() => setActiveTab('contatos')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'contatos' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>Contatos</button>
                        <button onClick={() => setActiveTab('variaveis')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'variaveis' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>Variáveis Dinâmicas</button>
                        <button onClick={() => setActiveTab('logs')} className={`px-3 py-2 font-medium text-sm rounded-md ${activeTab === 'logs' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>Logs</button>
                    </nav>
                </div>
                {/* Conteúdo da Aba Contatos */}
                {activeTab === 'contatos' && (
                    <div className="p-4">
                        <div className="flex justify-end mb-4">
                            <button onClick={() => exportar('xlsx')} className="flex items-center px-3 py-1 bg-gray-600 rounded-md font-semibold hover:bg-gray-700 text-xs"><ArrowDownTrayIcon className="h-4 w-4 mr-2" />Excel</button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-700 bg-opacity-50">
                                    <tr>
                                        <th className="p-3 font-semibold text-sm">Número</th>
                                        <th className="p-3 font-semibold text-sm text-center">Tentativas</th>
                                        <th className="p-3 font-semibold text-sm text-center">Follow-up</th>
                                        <th className="p-3 font-semibold text-sm">Status</th>
                                        <th className="p-3 font-semibold text-sm">Detalhes</th>
                                        <th className="p-3 font-semibold text-sm text-center">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campanha.contatos.map((numero) => {
                                        // Converter Map para objeto se necessário
                                        let resultadoObj = {};
                                        if (campanha.resultado) {
                                            if (campanha.resultado instanceof Map) {
                                                resultadoObj = Object.fromEntries(campanha.resultado);
                                            } else if (typeof campanha.resultado === 'object') {
                                                resultadoObj = campanha.resultado;
                                            }
                                        }
                                        const info = resultadoObj[numero] || {};
                                        const fuDisp = getFollowUpDisplayForContato(campanha, numero);
                                        // Tentar obter callId de diferentes lugares
                                        const callId = info.callId || info.response?.id || info.response?.data?.id || null;
                                        
                                        return (
                                            <tr key={numero} className="border-b border-gray-700">
                                                <td className="p-3 font-mono text-xs">{formatPhoneForDisplay(numero)}</td>
                                                <td className="p-3 text-center">{campanha.tentativas?.[numero] || 0}</td>
                                                <td className="p-3 text-center text-xs">
                                                    {fuDisp.text}
                                                </td>
                                                <td className="p-3"><StatusBadge status={info.status || 'em_fila'} /></td>
                                                <td className="p-3 text-xs text-red-400">{traduzirDetalhes(info.detalhes)}</td>
                                                <td className="p-3 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        {callId && (
                                                            <button 
                                                                onClick={() => openCallDetailsModal(callId)} 
                                                                className="p-2 text-gray-400 hover:text-purple-400 rounded-full hover:bg-gray-600 transition-colors" 
                                                                title="Ver detalhes da chamada"
                                                            >
                                                                <EyeIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                        <button 
                                                            onClick={() => reenviarContato(numero)} 
                                                            className="p-2 text-gray-400 hover:text-blue-400 rounded-full hover:bg-gray-600 transition-colors" 
                                                            title="Reenviar para este contato"
                                                        >
                                                            <PaperAirplaneIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {/* Conteúdo da Aba Variáveis Dinâmicas */}
                {activeTab === 'variaveis' && (
                    <div className="p-4">
                        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                            <div>
                                <h3 className="text-sm font-semibold text-white">Dados da planilha (variáveis dinâmicas)</h3>
                                <p className="text-xs text-gray-400">
                                    Colunas detectadas automaticamente a partir dos registros salvos na campanha.
                                </p>
                            </div>
                            <div className="w-full md:w-80">
                                <Label className="text-xs text-gray-400">Buscar valor nas variáveis</Label>
                                <Input
                                    type="text"
                                    value={variaveisSearch}
                                    onChange={(e) => setVariaveisSearch(e.target.value)}
                                    placeholder="Ex: CNPJ, nome, cidade..."
                                    className="mt-1 bg-gray-700 border-gray-600 text-white"
                                />
                            </div>
                        </div>

                        <div className="mb-3 text-xs text-gray-400">
                            Linhas: <span className="text-gray-200">{variaveisDinamicas.rows.length}</span>
                            {variaveisSearch.trim() && (
                                <> (filtradas de <span className="text-gray-200">{variaveisDinamicas.totalRows}</span>)</>
                            )}
                            {' · '}
                            Colunas: <span className="text-gray-200">{variaveisDinamicas.columns.length}</span>
                        </div>

                        {variaveisDinamicas.totalRows === 0 ? (
                            <p className="text-sm text-gray-400 py-6 text-center">
                                Esta campanha ainda não possui variáveis dinâmicas salvas.
                            </p>
                        ) : variaveisDinamicas.columns.length === 0 ? (
                            <p className="text-sm text-gray-400 py-6 text-center">
                                Não foi possível identificar colunas para exibição.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-gray-700 bg-opacity-50 text-gray-300">
                                        <tr>
                                            {variaveisDinamicas.columns.map((col) => (
                                                <th key={col} className="p-2 font-semibold whitespace-nowrap">
                                                    {col}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {variaveisDinamicas.rows.map((row, idx) => (
                                            <tr key={`${idx}-${row.telefone || ''}`} className="border-b border-gray-700">
                                                {variaveisDinamicas.columns.map((col) => (
                                                    <td key={`${idx}-${col}`} className="p-2 align-top text-gray-200">
                                                        <span className="block max-w-[360px] whitespace-pre-wrap break-words">
                                                            {renderCellValue(row[col])}
                                                        </span>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                {/* Conteúdo da Aba Logs - Chamadas dos contatos da campanha */}
                {activeTab === 'logs' && (
                    <div className="p-4">
                        <p className="text-sm text-gray-400 mb-4">Chamadas realizadas para os contatos desta campanha. Clique em uma linha para ver transcrição, áudio e estruturas de saída.</p>

                        <Card className="mb-4 bg-gray-800 border-gray-700">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg text-white">Filtros</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="max-w-xs">
                                    <Label className="text-xs text-gray-400">Número ligado</Label>
                                    <Input
                                        type="text"
                                        value={logsFilterCustomerNumber}
                                        onChange={e => setLogsFilterAndResetPage(setLogsFilterCustomerNumber, e.target.value)}
                                        placeholder="Ex: +5511999999999"
                                        className="mt-1 bg-gray-700 border-gray-600 text-white"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {campaignCallLogsLoading ? (
                            <p className="text-gray-400 text-center py-8">Carregando chamadas...</p>
                        ) : campaignDisplayLogs.length === 0 ? (
                            <p className="text-gray-400 text-center py-8">Nenhuma chamada encontrada para os contatos desta campanha.</p>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-gray-700 bg-opacity-50 text-gray-300">
                                            <tr>
                                                <th className="p-3 font-semibold">Assistente</th>
                                                <th className="p-3 font-semibold">Cliente</th>
                                                <th className="p-3 font-semibold">Status Final</th>
                                                <th className="p-3 font-semibold flex items-center gap-1">
                                                    <span>Início</span>
                                                    <CalendarIcon className="h-4 w-4 text-purple-400" />
                                                </th>
                                                <th className="p-3 font-semibold text-center">Duração</th>
                                                <th className="p-3 font-semibold text-center">Follow-up</th>
                                                <th className="p-3 font-semibold text-center">Custo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaignDisplayLogs.map((log) => (
                                                <tr
                                                    key={log._id}
                                                    onClick={() => {
                                                        const callWithAssistant = {
                                                            ...log,
                                                            assistantName: log.assistantName || log.assistant?.name || log.assistant?.nome || 'N/A'
                                                        };
                                                        setSelectedCampaignCall(callWithAssistant);
                                                    }}
                                                    className="cursor-pointer border-b border-gray-700 hover:bg-gray-700 hover:bg-opacity-40 transition-colors"
                                                >
                                                    <td className="p-3 font-medium flex items-center gap-2">
                                                        <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-purple-400" />
                                                        {log.assistantName}
                                                    </td>
                                                    <td className="p-3 font-mono text-xs">
                                                        {log.call?.customer?.number || log.call?.customer?.name || log.customer?.number || 'N/A'}
                                                    </td>
                                                    <td className="p-3">
                                                        <Badge className={getStatusBadgeClass(log.endedReason)}>
                                                            {traduzirEndedReason(log.endedReason)}
                                                        </Badge>
                                                    </td>
                                                    <td className="p-3">{log.formattedDate || 'N/A'}</td>
                                                    <td className="p-3 text-center font-mono text-xs">
                                                        <ClockIcon className="h-4 w-4 inline mr-1" />
                                                        {formatDuration(log.durationSeconds)}
                                                    </td>
                                                    <td className="p-3 text-center font-mono text-xs">
                                                        {log.followUpDisplayText}
                                                    </td>
                                                    <td className="p-3 text-center font-mono text-xs">
                                                        {typeof log.custoFinal === 'number' && log.custoFinal > 0
                                                            ? `$${log.custoFinal.toFixed(2)}`
                                                            : 'N/A'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {campaignLogsPagination.total_pages > 1 && (
                                    <div className="flex flex-col gap-3 border-t border-gray-700 bg-gray-800 bg-opacity-40 p-4 mt-0 md:flex-row md:items-center md:justify-between">
                                        <span className="text-sm text-gray-400">
                                            Página {campaignLogsCurrentPage} de {campaignLogsPagination.total_pages} (Mostrando {campaignDisplayLogs.length} de {campaignLogsPagination.total} registros)
                                        </span>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setCampaignLogsCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={campaignLogsCurrentPage === 1}
                                                className="bg-gray-700 text-white border-gray-600 hover:bg-gray-600"
                                            >
                                                Anterior
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setCampaignLogsCurrentPage(prev => Math.min(prev + 1, campaignLogsPagination.total_pages))}
                                                disabled={campaignLogsCurrentPage >= campaignLogsPagination.total_pages}
                                                className="bg-gray-700 text-white border-gray-600 hover:bg-gray-600"
                                            >
                                                Próxima
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        <div className="mt-8">
                            <h3 className="text-sm font-semibold text-white mb-2">Histórico de mensagens de limite</h3>
                            <p className="text-xs text-gray-400 mb-3">
                                Registro de tentativas de envio de mensagem quando o contato atinge o limite de follow-up.
                            </p>
                            {messageLogsLoading ? (
                                <p className="text-gray-400 text-center py-4">Carregando histórico de mensagens...</p>
                            ) : messageLogs.length === 0 ? (
                                <p className="text-gray-400 text-center py-4">Nenhum envio de mensagem registrado para esta campanha.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-gray-700 bg-opacity-50 text-gray-300">
                                            <tr>
                                                <th className="p-2 font-semibold">Data/Hora</th>
                                                <th className="p-2 font-semibold">Número</th>
                                                <th className="p-2 font-semibold">Status</th>
                                                <th className="p-2 font-semibold">Detalhe</th>
                                                <th className="p-2 font-semibold">Mensagem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {messageLogs.map((m) => (
                                                <tr key={m._id} className="border-b border-gray-700">
                                                    <td className="p-2">{formatDateTime(m.createdAt)}</td>
                                                    <td className="p-2 font-mono">{m.customerNumber || '-'}</td>
                                                    <td className="p-2">
                                                        <Badge className={
                                                            m.status === 'sent'
                                                                ? 'bg-green-600 text-white'
                                                                : m.status === 'failed'
                                                                    ? 'bg-red-600 text-white'
                                                                    : 'bg-yellow-600 text-white'
                                                        }>
                                                            {m.status === 'sent' ? 'Enviado' : m.status === 'failed' ? 'Falhou' : 'Ignorado'}
                                                        </Badge>
                                                    </td>
                                                    <td className="p-2">{m.reason || '-'}</td>
                                                    <td className="p-2 whitespace-pre-wrap break-words max-w-md">{m.messageText || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        {campanha.logs && campanha.logs.length > 0 && (
                            <details className="mt-6">
                                <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300">Logs de sistema da campanha</summary>
                                <pre className="bg-gray-900 text-xs text-gray-300 p-4 rounded-lg overflow-x-auto max-h-48 mt-2">
                                    {campanha.logs.join('\n')}
                                </pre>
                            </details>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
